import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { constructionReviewsService, ConstructionReview } from '../../services/construction-reviews.service';

interface ReviewCRState {
  // Current editing state
  current_editing_cr: ConstructionReview | null;
  
  // UI state for list management
  ui_state: {
    search_query: string;
    sort_by: 'created_at' | 'updated_at' | 'task_name';
    sort_order: 'asc' | 'desc';
    filter_by_date_range: {
      start?: string;
      end?: string;
    };
  };
  
  // Loading states
  loading: {
    save: boolean;
    delete: boolean;
    fetch: boolean;
  };
  
  // Error states
  errors: {
    save?: string;
    delete?: string;
    fetch?: string;
  };
}

const initialState: ReviewCRState = {
  current_editing_cr: null,
  ui_state: {
    search_query: '',
    sort_by: 'updated_at',
    sort_order: 'desc',
    filter_by_date_range: {}
  },
  loading: {
    save: false,
    delete: false,
    fetch: false
  },
  errors: {}
};

// Async thunks
export const saveCRChanges = createAsyncThunk(
  'reviewCR/save',
  async ({ id, data }: { id: string; data: { updated_cr: any; changes: any } }) => {
    return await constructionReviewsService.update(id, data);
  }
);

export const deleteCR = createAsyncThunk(
  'reviewCR/delete',
  async (id: string) => {
    await constructionReviewsService.delete(id);
    return id;
  }
);

const reviewCRSlice = createSlice({
  name: 'reviewCR',
  initialState,
  reducers: {
    // Set current editing CR
    setCurrentEditingCR: (state, action: PayloadAction<ConstructionReview | null>) => {
      state.current_editing_cr = action.payload;
    },
    
    // Update current editing CR data
    updateCurrentEditingCR: (state, action: PayloadAction<Partial<ConstructionReview>>) => {
      if (state.current_editing_cr) {
        state.current_editing_cr = { ...state.current_editing_cr, ...action.payload };
      }
    },
    
    // UI state updates
    updateUIState: (state, action: PayloadAction<Partial<ReviewCRState['ui_state']>>) => {
      state.ui_state = { ...state.ui_state, ...action.payload };
    },
    
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.ui_state.search_query = action.payload;
    },
    
    setSortBy: (state, action: PayloadAction<'created_at' | 'updated_at' | 'task_name'>) => {
      state.ui_state.sort_by = action.payload;
    },
    
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.ui_state.sort_order = action.payload;
    },
    
    setDateRange: (state, action: PayloadAction<{ start?: string; end?: string }>) => {
      state.ui_state.filter_by_date_range = action.payload;
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.errors = {};
    },
    
    // Reset state
    resetReviewCR: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Save CR changes
      .addCase(saveCRChanges.pending, (state) => {
        state.loading.save = true;
        state.errors.save = undefined;
      })
      .addCase(saveCRChanges.fulfilled, (state, action) => {
        state.loading.save = false;
        state.current_editing_cr = action.payload;
      })
      .addCase(saveCRChanges.rejected, (state, action) => {
        state.loading.save = false;
        state.errors.save = action.error.message;
      })
      
      // Delete CR
      .addCase(deleteCR.pending, (state) => {
        state.loading.delete = true;
        state.errors.delete = undefined;
      })
      .addCase(deleteCR.fulfilled, (state) => {
        state.loading.delete = false;
        state.current_editing_cr = null;
      })
      .addCase(deleteCR.rejected, (state, action) => {
        state.loading.delete = false;
        state.errors.delete = action.error.message;
      });
  }
});

export const {
  setCurrentEditingCR,
  updateCurrentEditingCR,
  updateUIState,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setDateRange,
  clearErrors,
  resetReviewCR
} = reviewCRSlice.actions;

export default reviewCRSlice.reducer;